// Client-side error beacon (port of frontend-reactjs src/helpers/errorReporter.js).
//
// Catches window "error" + "unhandledrejection" events and POSTs a small JSON
// payload to the REST API's /api/clientlog endpoint. The API logs each event
// as a [clientjs] line in its pm2 error log, where the pm2err digest
// (noderestapi cli/pm2err-digest.js) groups them under the CLIENTJS tier by
// app/module/message — so mpages18 webview errors show up in the same triage
// loop as server errors.
//
// Design rules:
// - each error signature is sent ONCE per page load, hard cap 10 events/load
// - sendBeacon with text/plain (CORS "simple request" — no preflight); wrapped
//   in try + fetch keepalive fallback (old Chrome throws on Blob beacons,
//   crbug.com/490015)
// - cross-origin "Script error." and third-party analytics frames are dropped
// - never throws: telemetry must never break the app
//
// BUILD id: set REACT_APP_BUILD_ID in the build pipeline (e.g.
// REACT_APP_BUILD_ID=$(git rev-parse --short HEAD) npm run build) so digest
// entries distinguish current-build bugs from stale cached bundles.

import { DecryptED } from "./Decrypt";

const ENDPOINT = `${process.env.REACT_APP_BASE_URL || ""}/api/clientlog`;
const APP = "mpages18";
const BUILD = process.env.REACT_APP_BUILD_ID || "unset";
const MAX_PER_LOAD = 10;

const sentSignatures = new Set();
let sentCount = 0;

function topFrame(stack) {
  if (!stack) return "";
  const lines = String(stack).split("\n");
  for (const l of lines) {
    if (l.includes("://") && !l.includes("node_modules")) {
      return l.trim().slice(-200);
    }
  }
  return (lines[1] || "").trim().slice(-200);
}

// mpages18 pages receive the sid in the URL (often AES-encrypted as enc-...),
// not in localStorage — best effort: ?sid= query param, decrypted if needed
function getSid() {
  try {
    const raw = new URLSearchParams(window.location.search).get("sid");
    if (!raw) return "";
    return String(DecryptED(raw)).slice(0, 12);
  } catch (e) {
    return "";
  }
}

export function reportClientError(message, extra) {
  try {
    if (!process.env.REACT_APP_BASE_URL) return;
    const msg = String(message || "unknown").slice(0, 400);
    const sig = msg.slice(0, 160);
    if (sentSignatures.has(sig) || sentCount >= MAX_PER_LOAD) return;
    sentSignatures.add(sig);
    sentCount++;
    const payload = JSON.stringify({
      app: APP,
      host: window.location.hostname,
      build: BUILD,
      module: (window.location.pathname.split("/")[1] || "root").slice(0, 60),
      sid: getSid(),
      kind: (extra && extra.kind) || "error",
      msg,
      frame: String((extra && extra.frame) || "").slice(0, 220),
      url: window.location.href.slice(0, 300),
    });
    let queued = false;
    try {
      if (navigator.sendBeacon) {
        queued = navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "text/plain" }));
      }
    } catch (e) {
      queued = false;
    }
    if (!queued) {
      fetch(ENDPOINT, {
        method: "POST",
        body: payload,
        keepalive: true,
        headers: { "Content-Type": "text/plain" },
      }).catch(() => {});
    }
  } catch (e) {
    // never break the app over telemetry
  }
}

export function initErrorReporter() {
  window.addEventListener("error", (e) => {
    // cross-origin scripts surface as a bare "Script error." — no information
    if (!e.message || e.message === "Script error.") return;
    const frame =
      topFrame(e.error && e.error.stack) ||
      `${(e.filename || "").slice(-160)}:${e.lineno}:${e.colno}`;
    // third-party analytics failing on ancient browsers is not our bug
    if (/cloudflareinsights\.com/.test(frame) || /cloudflareinsights\.com/.test(e.filename || "")) return;
    reportClientError(e.message, { frame, kind: "error" });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    reportClientError((r && (r.message || String(r))) || "unhandled promise rejection", {
      frame: topFrame(r && r.stack),
      kind: "promise",
    });
  });
}
